import {pushDataLayer} from '@optimaxdev/analytics/mobile';
import {productToEECCart, productToEECCartHto} from 'libs/EEC/convertData/productToEECCart';
import {QuoteItemType} from 'constants/flow/flowTypesCart';
import {pathOr} from 'ramda';
import {getPackage} from 'libs/EEC/packageType';

import {getBadge, getFrameUrl, isGlasses as isGlassesHelper} from '../EEC/productToEECProduct';
import {getStockStatus} from '../EEC/getStock';
import {RecommendationType} from '../../features/experiments/recommendation/types';
import {prepareImpressionData} from '../EEC/convertData/prepareData';
import {convertPriceEEC} from '../convertPrice/convertPrice';

type OptionType = {
    currencyCode: string;
    category: string;
    list: string;
    index: number;
};

type EECCartPropsType = {
    simpleProducts: QuoteItemType[];
    currencyCode?: string;
    isHTOEnabled?: boolean;
    coupons?: string[] | [];
};

const defaultOptions = {
    currencyCode: 'USD',
    index: 0,
    category: '',
    list: '',
};

/**
 * Send impression to GTM
 * @param {RecommendationType} recommendationData - data from dynamic yield
 * @param {OptionType} options - optional data
 */
export function sendEECImpression(
    recommendationData: Array<RecommendationType>,
    options: OptionType = defaultOptions,
) {
    const EECobj = {
        event: 'EEC_HP_impressions',
        ecommerce: {
            currencyCode: options.currencyCode || 'USD',
            impressions: recommendationData.map(({productData, sku}, index) => ({
                ...prepareImpressionData(productData, {index, sku}),
                price: convertPriceEEC(productData.price),
            })),
        },
    };

    pushDataLayer(EECobj);
}

/**
 * Send cart products to GTM
 * @param {EECCartPropsType} - props
 */
export function sendEECCartProducts({
    simpleProducts,
    currencyCode = 'USD',
    isHTOEnabled = false,
    coupons = [],
}: EECCartPropsType) {
    const ecommerceData = isHTOEnabled
        ? productToEECCartHto(simpleProducts)
        : productToEECCart(simpleProducts);

    const EECobj = {
        event: 'EEC_cart_products',
        ecommerce: {
            currencyCode,
            impressions: [
                ...ecommerceData.map((product, index) => {
                    const isGlasses = isGlassesHelper(simpleProducts[index].type);
                    const stockLevel = isGlasses
                        ? {dimension11: getStockStatus(simpleProducts[index])}
                        : null;
                    return {
                        ...product,
                        ...stockLevel,
                        dimension18: pathOr(false, ['isPremium'], simpleProducts[index]),
                        dimension20: getPackage(simpleProducts[index]),
                        dimension41: getBadge(simpleProducts[index]),
                        dimension44: getFrameUrl(simpleProducts[index]),
                        dimension72: product.quantity,
                    };
                }),
            ],
        },
        AppliedCoupon: coupons.join(),
    };

    pushDataLayer(EECobj);
}

/**
 * Send EEC event from product/configure pages to GTM
 * @param {object} ecommerceData - product data object
 */
export function sendEECProductEvent(ecommerceData) {
    const EECobj = {
        event: 'EEC_PDP_Product',
        ecommerce: ecommerceData,
    };
    pushDataLayer(EECobj);
}
